/**
 * In-app Help: step-by-step topics, one per module, opened from the "Help"
 * button on that module's screen. Plain data so the text is easy to keep up to
 * date as modules change, and tested so a topic cannot quietly go empty.
 */
export interface HelpTopic {
  title: string;
  intro: string;
  sections: { heading: string; steps: string[] }[];
}

export const HELP: Record<string, HelpTopic> = {
  billing: {
    title: 'Billing',
    intro:
      'Statements of account raised against sales orders, the payments received against them, and a statement of account per customer. These are not BIR invoices: an official invoice follows payment.',
    sections: [
      {
        heading: 'Find a customer’s statements',
        steps: [
          'Open Billing. The customer search is the first filter on top.',
          'Type part of the customer’s name (or code) and pick them from the list. The list below then shows only that customer’s statements.',
          'Use the search box beside it for a statement or order number, and the status filter for drafts, issued, paid and so on.',
          'Clear the customer with the × in the search box to see every customer again.',
        ],
      },
      {
        heading: 'Statement of account for a customer',
        steps: [
          'With a customer picked, click "Statement of account". It is also on the customer’s page, under Billings & Balance.',
          'It lists every issued statement for that customer across all their orders: date, statement no., sales order and project, amount billed, the payments received with their dates, the balance and the status.',
          'Filters on top: a from and to date (on the statement date; leave both empty for all time) and All / Paid / Pending. Pending means unpaid and partially paid.',
          'Status is Paid, Partially paid, Unpaid, or Overdue when an unpaid balance is past its due date.',
          'The totals show what was billed, what was paid and what is outstanding, with the outstanding amount aged the same way as Reports → AR Aging.',
          'Drafts and voided statements are never included. A payment only counts once it is confirmed; one still awaiting confirmation is shown beside the balance.',
        ],
      },
      {
        heading: 'Print or save as PDF',
        steps: [
          'Click Preview to open the PDF in a new tab, or Download to save it.',
          'It carries the company letterhead and the title STATEMENT OF ACCOUNT, and prints the period and filter you chose.',
          'Send it to the customer as it is: it shows amounts billed and paid only, never cost or profit.',
        ],
      },
      {
        heading: 'Single statements',
        steps: [
          'Click a statement number to open it: issue it, record a payment, confirm payments, or void it.',
          'Its own PDF prints the whole order, what this statement bills, and the payments already made on the contract.',
        ],
      },
    ],
  },
  pricebook: {
    title: 'Price Book',
    intro:
      'What our suppliers charge us, item by item, with every price they have ever quoted. These are supplier costs: only Finance, Management and Super Admin see them, and they never go into a customer document.',
    sections: [
      {
        heading: 'Find a price',
        steps: [
          'Open Suppliers → Price Book. It is there only for Finance, Management and Super Admin; other roles (Sales included) do not see it.',
          'Type a part number, model, brand or description in the search box. Spaces and dashes in part numbers do not matter.',
          'Narrow the list with the filters on top: supplier, brand, category, and Current / Stale.',
          'The Cost column is the current price: the newest version valid today. VAT-inc or VAT-ex is shown beside it — check it before you use the figure.',
        ],
      },
      {
        heading: 'Current and stale',
        steps: [
          'A price is current while today is within its valid-from and valid-until dates.',
          'A Stale badge means no version is valid today. The newest price is still shown so you have a figure, but confirm it with the supplier before quoting.',
          '"Not yet valid" means the newest list starts on a later date.',
        ],
      },
      {
        heading: 'Price history',
        steps: [
          'Click an item to see every version: cost, VAT basis, currency, validity, the price list it came from, who imported it and when.',
          'Nothing is ever overwritten. A new list with a different cost, VAT basis, currency or validity adds a version; a row that matches any earlier version adds nothing.',
          'When several versions are valid today, the one with the latest valid-from date is current, so an old list imported later never replaces a newer price.',
          'Where the supplier’s price list was stored with the import, download it from the history.',
        ],
      },
      {
        heading: 'Getting price lists in',
        steps: [
          'Anna imports supplier price lists through the integration API: a preview (dry run) first, then the real import once approved.',
          'The supplier must already be in Suppliers; an unknown supplier is reported, never created by the import.',
          'Every price needs its VAT basis, and a cost written with a currency ("$1,200") must match the row\u2019s currency. Costs like "1.234,56" are refused rather than guessed.',
          'Her key needs the rights "Search the Price Book" and "Import supplier price lists", ticked in Management → API Keys.',
        ],
      },
      {
        heading: 'Export',
        steps: [
          'Export downloads the filtered list to Excel with the current price of each item and who exported it.',
          'Treat the file as confidential: it carries supplier costs.',
        ],
      },
    ],
  },
};
